import { normalizeVraiFaux } from '../normalizers/normalizeVraiFaux';
import { HtmlWithKatex } from '../helpers/renderKatex';
import PrintEnonce from '../helpers/PrintEnonce';
import { consignePour, modeEffectif, tableEnListe } from '../helpers/saisiePolicy';
import { isVraiFaux } from '../helpers/groupQuestions';
import { besoinTableLibre } from '../helpers/reponseLibre';
import { styleGrille } from '../helpers/grilleReponses';

/**
 * Corps d'un exercice-tableau : Vrai/Faux OU tableau générique
 * (colonnesLibres… — colonnes arbitraires, ex. < / = / >).
 *
 * Mode feuille : tableau avec cases carrées vides à cocher.
 * Mode cahier : les tableaux convertibles passent par TableLibreBody (liste,
 * cf. PrintVraiFaux) ; ici ne restent que les tableaux d'association
 * (conservés, cellules sans cases : l'élève recopie sur son cahier).
 *
 * Exporté pour PrintVraiFauxGroup (mêmes règles par sous-question) et pour
 * CorrectionsSection (mode corrigé : `correctIndices` fourni → la bonne case
 * de chaque ligne est COCHÉE en vert ; prop absente = feuille élève vierge).
 */
export const VraiFauxBody = ({ lignes, colonnes, estVF, enCahier, correctIndices = null }) => {
    const enCorrige = Array.isArray(correctIndices);
    if (enCahier && estVF && !enCorrige) {
        return (
            <ol className="ep-vf-liste-cahier">
                {lignes.map((ligne, i) => (
                    <li key={i}>
                        <HtmlWithKatex html={ligne} />
                    </li>
                ))}
            </ol>
        );
    }
    return (
        <table className={`ep-vf-table${enCorrige ? ' ep-vf-table-corrige' : ''}`}>
            <thead>
                <tr>
                    <th className="ep-vf-header ep-vf-col-affirm">{estVF ? 'Affirmation' : ''}</th>
                    {colonnes.map((c) => (
                        <th key={c} className="ep-vf-header ep-vf-col-cell">{c}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {lignes.map((ligne, i) => (
                    <tr key={i}>
                        <td>
                            <HtmlWithKatex html={ligne} />
                        </td>
                        {colonnes.map((_, j) => (
                            <td key={j} className="ep-vf-cell">
                                {enCorrige ? (
                                    <span className={`ep-vf-box${correctIndices[i] === j ? ' ep-vf-box-coche' : ''}`}>
                                        {correctIndices[i] === j ? '✓' : ''}
                                    </span>
                                ) : (
                                    !enCahier && <span className="ep-vf-box" />
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

/** Repère a. b. c. (le même que dans le corrigé). */
const repereLettre = (i) => `${String.fromCharCode(97 + (i % 26))}.`;

/**
 * Corps d'un tableau rendu en LISTE : chaque ligne du tableau devient un
 * item « a. texte ……… » :
 *   - feuille (réponse libre) : courte ligne pointillée juste après le texte
 *     (l'élève écrit le libellé de la colonne) ; un vrai V/F ajoute une ligne
 *     pleine largeur pour la justification et reste sur une colonne ;
 *   - cahier : pas de ligne, l'élève répond sur son cahier (la consigne
 *     nomme les réponses possibles).
 * La liste est une `.ep-grille-reponses` : autant de colonnes que la place
 * réelle le permet (2 en demi-largeur, jusqu'à 4 en pleine largeur, zoom
 * compris), le renderer ne fournissant que la largeur nécessaire d'un item.
 */
export const TableLibreBody = ({ payload, enCahier }) => {
    const { lignes, vraiFaux, repW, besoin } = besoinTableLibre(payload, { sansLigne: enCahier });
    const uneColonne = vraiFaux && !enCahier;
    return (
        <ol
            className={`ep-vf-liste-libre ep-grille-reponses${uneColonne ? ' ep-grille-1col' : ''}`}
            style={styleGrille(besoin)}
        >
            {lignes.map((ligne, i) => (
                <li key={i} className="ep-vf-libre-item">
                    <div className="ep-vf-libre-rangee">
                        <span className="ep-vf-repere">{repereLettre(i)}</span>
                        <HtmlWithKatex className="ep-vf-libre-texte" html={ligne} />
                        {enCahier ? null : (
                            <span className="ep-answer-line ep-vf-reponse-line" style={{ '--ep-rep-w': `${repW}px` }} />
                        )}
                    </div>
                    {uneColonne ? <div className="ep-answer-line ep-vf-justif-line" /> : null}
                </li>
            ))}
        </ol>
    );
};

const PrintVraiFaux = ({ payload, num, saisieMode = 'sur_la_feuille', reponseLibre = false }) => {
    const { question, lignes, colonnes } = normalizeVraiFaux(payload, num);
    const estVF = isVraiFaux(payload);
    const enCahier = modeEffectif(payload, saisieMode) === 'cahier';
    const enListe = tableEnListe(payload, saisieMode, reponseLibre);

    return (
        <div className="ep-exo">
            <span className="ep-exo-num">{num}</span>
            <p className="ep-exo-consigne">{consignePour(payload, saisieMode, { libre: reponseLibre })}</p>
            {question && <PrintEnonce texte={question} elements={payload?.elements_additionnels?.elements} skipTables />}
            {enListe ? (
                <TableLibreBody payload={payload} enCahier={enCahier} />
            ) : (
                <VraiFauxBody lignes={lignes} colonnes={colonnes} estVF={estVF} enCahier={enCahier} />
            )}
        </div>
    );
};

export default PrintVraiFaux;
